import { CheckCircle2 } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useLogin, useNotify } from "ra-core";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

import { GRAVUN_BRAND } from "@/branding";
import { getSupabaseClient } from "../../atomic-crm/providers/supabase/supabase";

const INDUSTRIES = [
  "HVAC",
  "Plumbing",
  "Electrical",
  "Roofing",
  "Construction",
  "Cleaning",
  "Property Services",
  "Landscaping",
  "Agency",
  "Kids Programs",
  "Other",
];

interface PlanRow {
  id: string;
  key: string;
  name: string;
  description: string | null;
  price_monthly_cents: number | null;
  highlight: boolean;
  sort_order: number;
}

const inputClass =
  "h-11 w-full rounded-[10px] border border-input bg-card px-3 text-[14px] outline-none focus-visible:ring-2 focus-visible:ring-ring";

/**
 * Public SaaS signup (/#/signup?plan=<key>), the landing point for the
 * gravun.com pricing CTAs.
 *
 * One page, three visible steps: account, company, plan. The chosen plan
 * arrives via ?plan= and is preselected but changeable. Everything is
 * provisioned server-side by handle_new_user (organization, workspace,
 * owner membership, 14-day trial on the chosen plan); paying happens
 * afterwards through Stripe Checkout from Plan & Billing, and only the
 * Stripe webhook activates a paid state.
 */
export const SaasSignupPage = () => {
  const login = useLogin();
  const notify = useNotify();

  const planFromUrl = useMemo(() => {
    const hash = window.location.hash;
    const query = hash.includes("?") ? hash.slice(hash.indexOf("?") + 1) : "";
    return new URLSearchParams(query).get("plan") ?? "";
  }, []);

  // Raw client read: this page renders BEFORE authentication, so it uses
  // the anon role's read access to the public plan catalogue directly.
  const [plans, setPlans] = useState<PlanRow[] | null>(null);
  useEffect(() => {
    getSupabaseClient()
      .from("plans")
      .select(
        "id, key, name, description, price_monthly_cents, highlight, sort_order",
      )
      .eq("active", true)
      .order("sort_order")
      .then(({ data }) => setPlans((data as PlanRow[]) ?? []));
  }, []);

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [company, setCompany] = useState("");
  const [industry, setIndustry] = useState("");
  const [planKey, setPlanKey] = useState(planFromUrl);
  const [busy, setBusy] = useState(false);

  const effectivePlan =
    (plans ?? []).find((p) => p.key === planKey)?.key ??
    (plans ?? []).find((p) => p.highlight)?.key ??
    (plans ?? [])[0]?.key ??
    "";

  const submit = async () => {
    if (!firstName.trim() || !email.trim() || password.length < 8) {
      notify(
        "Fill in your name, email and a password of at least 8 characters.",
        {
          type: "warning",
        },
      );
      return;
    }
    if (!company.trim()) {
      notify("What is your company called?", { type: "warning" });
      return;
    }
    setBusy(true);
    const { error } = await getSupabaseClient().auth.signUp({
      email: email.trim(),
      password,
      options: {
        data: {
          first_name: firstName.trim(),
          last_name: lastName.trim(),
          company_name: company.trim(),
          industry: industry || null,
          plan: effectivePlan,
        },
      },
    });
    if (error) {
      setBusy(false);
      notify(error.message, { type: "error" });
      return;
    }
    try {
      await login({
        email: email.trim(),
        password,
        redirectTo: "/welcome",
      });
      // Some auth providers ignore redirectTo and land on the dashboard;
      // a brand-new business should always see the onboarding page first.
      window.location.hash = "#/welcome";
    } catch {
      // Email confirmation may be required; the login page explains it.
      setBusy(false);
      notify("Account created. Check your email to confirm, then sign in.", {
        type: "info",
      });
      window.location.hash = "#/login";
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="mx-auto flex w-full max-w-3xl flex-col gap-6 px-4 py-8 sm:py-12">
        <img
          className="h-8 w-auto self-center dark:hidden"
          src={GRAVUN_BRAND.wordmarkNavy}
          alt={GRAVUN_BRAND.name}
        />
        <div className="text-center">
          <h1 className="text-2xl font-semibold tracking-tight">
            Start your 14-day free trial
          </h1>
          <p className="mt-1 text-[14px] text-muted-foreground">
            No card needed to start. Pick a plan now, pay when the trial ends.
          </p>
        </div>

        <div className="rounded-2xl border border-border-subtle bg-card p-6 sm:p-8">
          <h2 className="text-[13px] font-semibold uppercase tracking-wide text-muted-foreground">
            1. Your account
          </h2>
          <div className="mt-3 grid gap-3 sm:grid-cols-2">
            <input
              className={inputClass}
              placeholder="First name"
              autoComplete="given-name"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
            />
            <input
              className={inputClass}
              placeholder="Last name"
              autoComplete="family-name"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
            />
            <input
              className={inputClass}
              placeholder="Work email"
              type="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            <input
              className={inputClass}
              placeholder="Password (8+ characters)"
              type="password"
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </div>

          <h2 className="mt-6 text-[13px] font-semibold uppercase tracking-wide text-muted-foreground">
            2. Your company
          </h2>
          <div className="mt-3 grid gap-3 sm:grid-cols-2">
            <input
              className={inputClass}
              placeholder="Company name"
              autoComplete="organization"
              value={company}
              onChange={(e) => setCompany(e.target.value)}
            />
            <select
              className={inputClass}
              value={industry}
              onChange={(e) => setIndustry(e.target.value)}
            >
              <option value="">Industry (optional)</option>
              {INDUSTRIES.map((entry) => (
                <option key={entry} value={entry}>
                  {entry}
                </option>
              ))}
            </select>
          </div>

          <h2 className="mt-6 text-[13px] font-semibold uppercase tracking-wide text-muted-foreground">
            3. Your plan
          </h2>
          <div className="mt-3 grid gap-3 sm:grid-cols-3">
            {(plans ?? []).map((plan) => {
              const selected = plan.key === effectivePlan;
              return (
                <button
                  key={plan.id}
                  type="button"
                  onClick={() => setPlanKey(plan.key)}
                  className={cn(
                    "flex flex-col gap-1 rounded-xl border p-4 text-left transition-colors",
                    selected
                      ? "border-primary ring-2 ring-primary/30"
                      : "border-border-subtle hover:border-border",
                  )}
                >
                  <span className="flex items-center gap-1.5 text-[15px] font-semibold">
                    {plan.name}
                    {selected ? (
                      <CheckCircle2 className="size-4 text-primary" />
                    ) : null}
                  </span>
                  {plan.price_monthly_cents != null ? (
                    <span className="text-[13px] text-muted-foreground">
                      ${(plan.price_monthly_cents / 100).toFixed(0)}
                      /user/month
                    </span>
                  ) : null}
                  {plan.description ? (
                    <span className="text-[12px] text-muted-foreground">
                      {plan.description}
                    </span>
                  ) : null}
                </button>
              );
            })}
          </div>

          <Button
            size="lg"
            className="mt-6 w-full"
            disabled={busy}
            onClick={() => void submit()}
          >
            {busy ? "Creating your workspace…" : "Create my account"}
          </Button>
          <p className="mt-3 text-center text-[12px] text-muted-foreground">
            Already using Gravun?{" "}
            <a className="underline underline-offset-2" href="#/login">
              Sign in
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

SaasSignupPage.path = "/signup";
